import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DecodedTransaction } from '@sol-tx-visualizer/tx-decode';
import { fetchTransaction } from '../lib/rpc';
import CpiTree from './CpiTree';
import CuTimeline from './CuTimeline';
import AccountDiff from './AccountDiff';

export default function TxDetailPage() {
  const { signature } = useParams<{ signature: string }>();
  const [tx, setTx] = useState<DecodedTransaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'cpi' | 'cu' | 'diff'>('cpi');

  useEffect(() => {
    if (!signature) return;

    setLoading(true);
    setError(null);
    setTx(null);
    setActiveTab('cpi');

    fetchTransaction(signature)
      .then((decoded) => setTx(decoded))
      .catch((err) => setError(err instanceof Error ? err.message : 'Unknown error'))
      .finally(() => setLoading(false));
  }, [signature]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-slate-400">
          <Spinner />
          <span className="text-sm">Loading transaction…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300 transition-colors"
        >
          ← Back to home
        </Link>
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-rose-400 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!tx) {
    return (
      <div className="space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300 transition-colors"
        >
          ← Back to home
        </Link>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-8 text-center text-slate-500 text-sm">
          No transaction data.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300 transition-colors"
      >
        ← Back to home
      </Link>

      {/* Transaction Summary */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryItem label="Signature" value={`${tx.signature.slice(0, 8)}…${tx.signature.slice(-8)}`} />
        <SummaryItem label="Slot" value={tx.slot.toLocaleString()} />
        <SummaryItem
          label="Status"
          value={tx.success ? 'Success' : 'Failed'}
          valueClass={tx.success ? 'text-emerald-400' : 'text-rose-400'}
        />
        <SummaryItem label="Fee" value={`${(tx.fee / 1e9).toFixed(6)} SOL`} />
      </div>

      {/* CU Warning */}
      {tx.cuBreakdown.overProvisioned && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-amber-400 font-medium text-sm">
            <span>⚡</span>
            <span>Over-provisioned CU limit</span>
          </div>
          <p className="text-amber-400/80 text-xs mt-1">
            Limit: {tx.cuBreakdown.limit.toLocaleString()} → Recommend: {tx.cuBreakdown.recommendedLimit.toLocaleString()}
            {' '}(est. savings: {tx.cuBreakdown.estimatedSavingsPercent.toFixed(1)}%)
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-slate-900 border border-slate-800 p-1 w-fit">
        <TabButton active={activeTab === 'cpi'} onClick={() => setActiveTab('cpi')}>
          CPI Tree
        </TabButton>
        <TabButton active={activeTab === 'cu'} onClick={() => setActiveTab('cu')}>
          CU Timeline
        </TabButton>
        <TabButton active={activeTab === 'diff'} onClick={() => setActiveTab('diff')}>
          Account Diffs
        </TabButton>
      </div>

      {/* Content */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        {activeTab === 'cpi' && <CpiTree nodes={tx.cpiTree} />}
        {activeTab === 'cu' && <CuTimeline breakdown={tx.cuBreakdown} />}
        {activeTab === 'diff' && <AccountDiff deltas={tx.accountDeltas} />}
      </div>
    </div>
  );
}

function SummaryItem({
  label,
  value,
  valueClass = 'text-slate-200',
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm font-mono mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active
          ? 'bg-slate-700 text-slate-100'
          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
      }`}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
